import { useCallback, useRef } from 'react';
import { ERROR_API_CALL } from '@/consts/events';
import { CONSENT_ACCESS_CMS_PILOT } from '@/consts/consentTypes';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRegistration } from '@/context/RegistrationContext';
import {
  apiClient,
  HelloServerErrorCodes,
  type ConsentMetadata,
  type Locale,
} from '@/services/api';
import type { UserData, TrackType } from '@/types/registration';
import { toIsoBirthDate } from '@/utils/date';
import { toE164 } from '@/utils/phone';
import {
  SIGNUP_PLACEHOLDER_STATE,
  SIGNUP_PLACEHOLDER_ZIP,
  generateDummyPassword,
} from '@/utils/signup';
import type { WregIntakeForm } from './screens/wregIntakeFields';
import { useTranslation } from 'react-i18next';

const WREG_TRACK: TrackType = 'eCKM';

// wregCmsPoll's errStatus only understands CmsApiError, so it reports 'unknown'
// for everything hello-server returns. What matters here is hello-server's own
// error `number`, because that is the ladder that says how far the request got:
// 2000 form binding, 1000 user exists, 1009 promo, 13000 org unit, 13001 client.
// Untrusted wire data, so it is validated rather than asserted.
export const signupErrNumber = (err: unknown): number | undefined => {
  const raw = (err as { response?: { data?: { number?: unknown } } })?.response?.data?.number;
  const code = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  return Number.isFinite(code) ? code : undefined;
};

export const signupErrStatus = (err: unknown): string | number => {
  const res = (err as { response?: { status?: unknown } })?.response;
  const code = signupErrNumber(err);
  const status = typeof res?.status === 'number' ? res.status : undefined;
  if (status && code !== undefined) return `${status}/${code}`;
  if (status) return status;
  return code ?? 'unknown';
};

// The promo rung of the same ladder. hello-server has never named a dedicated
// cap error code, and a disabled code returned a bodyless 404 on 2026-09-02,
// which is a different case and deliberately does not match. This is the
// secondary signal: the authoritative one is `capReached` on the promo-code
// lookup, which arrives on a 200 long before signup.
export const signupPromoRejected = (err: unknown): boolean =>
  signupErrNumber(err) === HelloServerErrorCodes.PromoCodeRejected;

export const promoCodeUnusable = (err: unknown): boolean => {
  if (signupPromoRejected(err)) return true;
  const status = (err as { response?: { status?: unknown } })?.response?.status;
  return status === 404;
};

export type WregSignupFailure = 'invalid-dob' | 'invalid-phone' | 'promo-cap' | 'failed';

// Carries the UserData alongside the token because setUserData is React state: a caller that signs
// up and then ships in the same callback cannot see the context write yet (RND-38229), and
// useWregShipping bails to { ok: true, sent: false } on a missing token, which reports success while
// sending nothing.
export type WregSignupOutcome =
  | { ok: true; authToken: string; userData: UserData }
  | { ok: false; reason: WregSignupFailure };

export const wregSignupIdentity = (form: WregIntakeForm): string =>
  [form.email.trim().toLowerCase(), form.phone].join('|');

export const useWregSignup = () => {
  const { trackError, identifyUser } = useAnalytics();
  const { webregUserId, sampleGroup, utmParams, registrationData, setUserData } = useRegistration();
  const { i18n } = useTranslation();

  // hello-server rejects a second signup for the same person with a generic
  // code 1000, and there is no already-registered screen to route that to. So
  // the account is created once per identity and the token reused, which also
  // makes an error-screen retry safe.
  const created = useRef<{ identity: string; authToken: string; userData: UserData } | null>(
    null,
  );

  // The cap is reported once per identity. Signup is still retried, because a
  // cap can lift, but the error modal's retry re-runs this whole callback and
  // one member hitting retry five times is one capped registration, not five.
  const capped = useRef<string | null>(null);

  const submit = useCallback(
    async (form: WregIntakeForm): Promise<WregSignupOutcome> => {
      const identity = wregSignupIdentity(form);
      if (created.current?.identity === identity) {
        return {
          ok: true,
          authToken: created.current.authToken,
          userData: created.current.userData,
        };
      }

      const birthDate = toIsoBirthDate(form.dob);
      if (!birthDate) return { ok: false, reason: 'invalid-dob' };

      // A leading zero is an Israeli tester, per RND-37405. The intake mask
      // keeps all ten digits so this is the only place the mapping happens.
      const phone = toE164(form.phone);
      if (!phone) return { ok: false, reason: 'invalid-phone' };

      const consents: ConsentMetadata[] = [
        {
          version: 1,
          status: 'consent',
          dateReported: Date.now(),
          source: 'access_registration',
          userMetadataObjectType: CONSENT_ACCESS_CMS_PILOT,
        },
      ];

      try {
        const response = await apiClient.signup({
          email: form.email.trim(),
          password: generateDummyPassword(),
          phoneNumber: phone,
          personalDetails: {
            firstname: form.first.trim(),
            lastname: form.last.trim(),
            ['date_of_birth']: birthDate,
            city: '',
            phonenumber: form.phone,
            state: SIGNUP_PLACEHOLDER_STATE,
            streetaddress: '',
            zipcode: SIGNUP_PLACEHOLDER_ZIP,
          },
          enableSmsMarketing: false,
          registrationMetadata: { webregUserId, ...(utmParams ?? {}) },
          locale: i18n.language as Locale,
          promoCode: registrationData.promoCodeData?.code ?? '',
          sampleGroup,
          consentsMetadata: { consents },
          cmsAccessEnrollment: { tracks: [WREG_TRACK] },
        });

        const userData: UserData = {
          authToken: response.authToken,
          solutions: (response.promoCode?.solutions ?? []).filter(
            (s) => s.hasDevice && s.hasShipping,
          ),
          email: response.user.email || form.email.trim(),
          phonenumber: form.phone,
          firstName: form.first.trim(),
          lastName: form.last.trim(),
          analyticsID: response.user.analyticsID,
          OTPLink: response.otpLink,
        };
        created.current = { identity, authToken: response.authToken, userData };
        setUserData(userData);
        identifyUser(response.user.analyticsID);
        return { ok: true, authToken: response.authToken, userData };
      } catch (err) {
        trackError({
          event: ERROR_API_CALL,
          properties: { service: 'wreg_signup', track: WREG_TRACK, status: signupErrStatus(err) },
        });
        const cap = signupPromoRejected(err) || registrationData.promoCodeData?.capReached === true;
        if (!cap) return { ok: false, reason: 'failed' };
        if (capped.current === identity) return { ok: false, reason: 'promo-cap' };
        // Reporting only, and the member is already blocked, so a failure to
        // record must not turn into a second failure on top of the first. The
        // identity is marked only once the record lands, so a record that failed
        // is retried on the member's next attempt rather than lost.
        try {
          await apiClient.recordFailedRegistration({
            webregUserId,
            identifier: form.email.trim().toLowerCase(),
            reason: 'CAP_REACHED',
            promoCode: registrationData.promoCodeData?.code ?? '',
          });
          capped.current = identity;
        } catch (recordErr) {
          trackError({
            event: ERROR_API_CALL,
            properties: {
              service: 'wreg_registration_failure',
              track: WREG_TRACK,
              status: signupErrStatus(recordErr),
            },
          });
        }
        return { ok: false, reason: 'promo-cap' };
      }
    },
    [
      i18n.language,
      identifyUser,
      registrationData.promoCodeData?.capReached,
      registrationData.promoCodeData?.code,
      sampleGroup,
      setUserData,
      trackError,
      utmParams,
      webregUserId,
    ],
  );

  // Consents answered after the account exists cannot ride the signup payload,
  // so they go to the batch endpoint instead. A failure here must not strand the
  // member: the answer is an attestation, and the flow is worth more than the
  // record of it.
  const recordConsents = useCallback(
    async (consents: ConsentMetadata[]): Promise<boolean> => {
      const authToken = created.current?.authToken;
      if (!authToken || consents.length === 0) return false;
      try {
        await apiClient.postConsents(consents, authToken);
        return true;
      } catch (err) {
        trackError({
          event: ERROR_API_CALL,
          properties: { service: 'wreg_consents', track: WREG_TRACK, status: signupErrStatus(err) },
        });
        return false;
      }
    },
    [trackError],
  );

  return { submit, recordConsents };
};
